
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { PROJECT_STATUSES, statusTone } from "@/lib/api";
import AppShell from "@/components/AppShell";
import { DASH, NEW_PROJECT } from "@/constants/testIds";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { Plus, Search, Trash2, ArrowRight, Sparkles, Eye } from "lucide-react";

export default function Dashboard() {
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ customer_name: "", business_name: "", phone: "", email: "", address: "" });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/projects", { params });
      setProjects(data);
    } catch (e) {
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  
  useEffect(() => {
    load();
  }, [statusFilter]);
  
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [q]);

  const counts = PROJECT_STATUSES.reduce((acc, s) => {
    acc[s] = projects.filter(p => p.status === s).length;
    return acc;
  }, {});

  const submitNew = async () => {
    if (!form.business_name.trim()) { toast.error("Business name is required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/projects", form);
      toast.success("Project created");
      setOpen(false);
      setForm({ customer_name: "", business_name: "", phone: "", email: "", address: "" });
      nav(`/projects/${data.id}`);
    } catch (e) {
      toast.error("Could not create project");
    } finally { setSubmitting(false); }
  };

  const onDelete = async (id, ev) => {
    ev?.stopPropagation();
    if (!window.confirm("Delete this project? This cannot be undone.")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects(ps => ps.filter(p => p.id !== id));
      toast.success("Project deleted");
    } catch { toast.error("Delete failed"); }
  };

  return (
    <AppShell right={
      <Button
        data-testid={DASH.newProjectBtn}
        onClick={() => setOpen(true)}
        className="bg-brand hover:bg-brand-hover text-white rounded-md gap-2 h-9"
      >
        <Plus className="w-4 h-4" /> New Quote
      </Button>
    }>
      <div data-testid={DASH.page} className="enter-up">
        {/* Title row */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-8">
          <div>
            <div className="label-tag mb-2">Internal Dashboard</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight">Sign Quote Projects</h1>
            <p className="text-zinc-500 mt-2 max-w-xl">
              Manage active quotes, mockups and production pipeline. Click any project to enter the workspace.
            </p>
          </div>
        </div>

        {/* Pipeline strip */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
          {PROJECT_STATUSES.map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? "all" : s)}
              className={`text-left p-3 border rounded-md transition-colors ${
                statusFilter === s
                  ? "border-brand bg-brand/10"
                  : "border-zinc-800 hover:border-zinc-700 bg-[#121214]"
              }`}
            >
              <div className="label-tag text-[9px] truncate">{s}</div>
              <div className="font-mono text-2xl mt-1">{counts[s] || 0}</div>
            </button>
          ))}
        </div>

        {/* Search + filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              data-testid={DASH.searchInput}
              placeholder="Search customer, business, email, phone…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full h-10 pl-10 pr-3 bg-[#121214] border border-zinc-800 rounded-md text-sm placeholder:text-zinc-600 focus:border-brand focus:outline-none transition-colors"
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid={DASH.statusFilter} className="w-full sm:w-56 bg-[#121214] border-zinc-800 h-10">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {PROJECT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Projects list */}
        <div data-testid={DASH.projectsList}>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="h-44 surface-card rounded-md animate-pulse" />
              ))}
            </div>
          ) : projects.length === 0 ? (
            <div data-testid={DASH.emptyState} className="surface-card rounded-md p-12 text-center">
              <Sparkles className="w-8 h-8 mx-auto text-brand mb-4" />
              <h3 className="font-heading text-xl font-bold">No projects yet</h3>
              <p className="text-sm text-zinc-500 mt-1 mb-6 max-w-md mx-auto">
                Start a new quote to upload a storefront photo, enter sign measurements and generate a customer-ready proposal.
              </p>
              <Button onClick={() => setOpen(true)} className="bg-brand hover:bg-brand-hover gap-2">
                <Plus className="w-4 h-4" /> Create first quote
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.map((p) => (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  data-testid={DASH.projectCard(p.id)}
                  className="group surface-card rounded-md p-5 hover:border-brand transition-colors flex flex-col"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="label-tag truncate">{p.customer_name || "—"}</div>
                      <h3 className="font-heading text-xl font-bold tracking-tight truncate mt-1">{p.business_name || "Untitled"}</h3>
                    </div>
                    <span className={`text-[10px] tracking-[0.18em] uppercase font-semibold border rounded-sm px-2 py-1 whitespace-nowrap ${statusTone(p.status)}`}>
                      {p.status}
                    </span>
                  </div>

                  <div className="mt-4 text-xs text-zinc-500 space-y-1">
                    <div className="truncate">{p.email || "—"}</div>
                    <div className="truncate">{p.phone || "—"}</div>
                    <div className="truncate">{p.address || "—"}</div>
                  </div>

                  <div className="mt-5 pt-4 border-t border-zinc-800 flex items-center justify-between text-xs">
                    <div className="font-mono text-zinc-400">
                      ${(p.pricing?.total || 0).toFixed(0).toLocaleString()}
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        data-testid={DASH.deleteProjectBtn(p.id)}
                        onClick={(e) => onDelete(p.id, e)}
                        className="text-zinc-500 hover:text-rose-400 transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                      <div
                        data-testid={DASH.openProjectBtn(p.id)}
                        className="inline-flex items-center gap-1 text-brand group-hover:gap-2 transition-all font-semibold"
                      >
                        Open <ArrowRight className="w-3.5 h-3.5" />
                      </div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="mt-12 text-[11px] text-zinc-600 flex items-center gap-2 justify-end">
          <Eye className="w-3 h-3" /> Internal use only — for 360 Digital Marketing staff
        </div>
      </div>

      {/* New Project Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid={NEW_PROJECT.dialog} className="bg-[#121214] border-zinc-800 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">New Quote Project</DialogTitle>
            <DialogDescription className="text-zinc-500">Enter basic customer info. You can edit everything later inside the workspace.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div>
              <Label className="label-tag">Business Name *</Label>
              <Input data-testid={NEW_PROJECT.businessName} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"
                value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                placeholder="Tina's Mexican Grill" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="label-tag">Customer Name</Label>
                <Input data-testid={NEW_PROJECT.customerName} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"
                  value={form.customer_name}
                  onChange={(e) => setForm({ ...form, customer_name: e.target.value })} />
              </div>
              <div>
                <Label className="label-tag">Phone</Label>
                <Input data-testid={NEW_PROJECT.phone} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="label-tag">Email</Label>
              <Input data-testid={NEW_PROJECT.email} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div>
              <Label className="label-tag">Storefront Address</Label>
              <Input data-testid={NEW_PROJECT.address} className="mt-1.5 bg-[#0c0c0e] border-zinc-800"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid={NEW_PROJECT.cancelBtn} variant="outline" onClick={() => setOpen(false)} className="border-zinc-800">Cancel</Button>
            <Button data-testid={NEW_PROJECT.submitBtn} onClick={submitNew} disabled={submitting} className="bg-brand hover:bg-brand-hover">
              {submitting ? "Creating…" : "Create & open workspace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
